import React, { useState, useEffect } from 'react'
import _assign from 'lodash/assign'
import _size from 'lodash/size'
import _toNumber from 'lodash/toNumber'
import _compact from 'lodash/compact'
import _get from 'lodash/get'
import { fromJS, Map, List } from 'immutable'
import { connect as connectRedux, useSelector } from 'react-redux'
import compose from 'recompose/compose'
import { useLazyQuery } from '@apollo/client'
import { getBoundActions } from '../../../actions/index'
import { TYPE_CONTENT_SERIES } from '../../../services/content-type/index'
import { DETAIL_PAGE_STATIC_KEY } from '../../../services/detail/index'
import {
  isGuide,
  isEpisode,
  getLocationType,
  getFeaturedContent,
  createTitleGroupId,
  getFormattedEpisodeData,
  isSubtypeEvent,
} from '../../../services/content/index'
import { getPrimary } from '../../../services/languages/index'
import { FILTER_APPLIED_GA4_EVENT } from '../../../services/event-tracking/index'
import { selectResolverQueryParamByKey } from '../../../services/resolver/selectors'
import { HISTORY_METHOD_REPLACE, historyRedirect } from '../../../services/navigation/index'
import { GET_EPISODES } from '../../../services/content/query'
import SelectField from '../../SelectField/index'
import { H5 } from '../../Heading/index'
import Button, { TYPES } from '../../Button/index'
import { hasLiveAccessEntitlement } from '../../../services/subscription/index'
import { URL_GAIA_PLUS } from '../../../services/url/constants'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { addNewRelicAction } from '../../../services/app/_newrelic'
import DetailEpisodeList from '../DetailEpisodeList'
import { getDetailPageUiLocationGQL } from '../utils'

/**
 * The Detail Series Seasons Join Banner
 * @param {object} props - The props object
 * @param {object} props.auth - The auth object
 * @param {object} props.staticText - The static text object
 * @param {object} props.detailData - The detail data object
 * @returns {JSX.Element|null} The Detail Series Seasons Join Banner component
 */
function DetailSeriesSeasonsJoinBanner (props) {
  const {
    auth,
    staticText,
    detailData,
    isGuideDetailPage,
  } = props

  const seriesTitle = detailData.get('title') || detailData.get('seriesTitle') || null
  const isLoggedIn = getAuthIsLoggedIn(auth)

  return seriesTitle ? (
    <div className="detail-series-seasons__join-banner">
      <div className="detail-series-seasons__join-banner-container">
        <div className="detail-series-seasons__join-banner-description">
          <div className="detail-series-seasons__join-banner-subheader">
            {`${staticText.get(isGuideDetailPage ? 'toWatchAllLessons' : 'toWatchAllEpisodes')} `}<span>{seriesTitle}</span>
          </div>
          <div className="detail-series-seasons__join-banner-header">
            {staticText.get(isLoggedIn ? 'upgradeToGaia' : 'signUpToAccess')}
          </div>
        </div>
        <Button
          className="detail-series-seasons__join-banner-button"
          type={TYPES.PRIMARY_FANCY}
          url={URL_GAIA_PLUS}
        >
          {staticText.get('getGaiaPlus')}
        </Button>
      </div>
    </div>
  ) : null
}

/**
 * The Detail Series Seasons Episodes List GraphQL for the Detail Pages
 * @returns {JSX.Element} The Detail Page Series Seasons Episodes
 */
function DetailSeriesSeasons (props) {
  const {
    auth,
    history,
    isEvent,
    detailData,
    staticText,
    userLanguage,
    hasLiveAccess,
    resolverLocation,
    isGuideDetailPage,
    isEpisodeDetailPage,
    setEventDefault,
  } = props
  const contentId = isEpisodeDetailPage ? detailData.get('seriesId') : detailData.get('contentId')
  const querySeason = useSelector(selectResolverQueryParamByKey('season'))
  const featuredContent = getFeaturedContent({ data: detailData })
  const currentSeason = isEpisodeDetailPage ? detailData.get('seasonNumber', 1) : featuredContent.get('seasonNumber', 1)
  const detailLocationType = getLocationType({ data: detailData })
  const uiLocation = getDetailPageUiLocationGQL(detailLocationType)
  const titleGroupIds = []
  const [selectedSeason, setSelectedSeason] = useState(Number(querySeason) || currentSeason)
  const [getEpisodesData, { data, loading }] = useLazyQuery(GET_EPISODES)
  const episodeData = fromJS(_get(data, ['contentList', 'content', 0], {}) || {})
  const formattedEpisodeData = getFormattedEpisodeData(episodeData)
  const episodeTileData = formattedEpisodeData.get('episodes') || List()
  const seasonNums = formattedEpisodeData.get('seasonNumbers') || List()
  const isGuideOrEvent = isEvent || isGuideDetailPage
  const [availableEpisodeList, setAvailableEpisodeList] = useState(List())
  const [showAvailableEpisodes, setShowAvailableEpisodes] = useState(false)

  if (contentId) {
    titleGroupIds.push(createTitleGroupId(contentId, TYPE_CONTENT_SERIES))
  }

  useEffect(() => {
    if (_size(titleGroupIds)) {
      getEpisodesData({
        variables: {
          language: [userLanguage],
          imageLanguages: [userLanguage, '_primary'],
          titleGroupIds: _compact(titleGroupIds),
          seasonNumber: _toNumber(selectedSeason) || 1,
        },
        context: {
          jwt: auth.get('jwt'),
        },
      }).catch((e) => {
        const networkError = e && e.networkError
        const httpStatus = networkError && networkError.statusCode
        addNewRelicAction('GetEpisodesQueryFailed', {
          contentId,
          seasonNumber: _toNumber(selectedSeason) || 1,
          errorMessage: e instanceof Error ? e.message : String(e),
          ...(httpStatus != null ? { httpStatus } : {}),
        })
      })
    }
  }, [contentId, userLanguage, selectedSeason, currentSeason])

  useEffect(() => {
    if (isGuideOrEvent && episodeTileData.size > 0 && availableEpisodeList.size === 0) {
      const availableEpisodes = episodeTileData.filter((episode) => episode.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'allowed'], false)) || List()
      const showAvailableEpisodesFlag = availableEpisodes.size > 0
        && availableEpisodes.size !== episodeTileData.size
      setAvailableEpisodeList(availableEpisodes)
      setShowAvailableEpisodes(showAvailableEpisodesFlag)
    }
  }, [episodeTileData])

  const seasonTitle = staticText.get('season')
  const seasonsOptions = seasonNums.map((seasonNumber) => fromJS({
    name: `${seasonTitle} ${seasonNumber}`,
    value: seasonNumber,
  }))

  const handleSeasonSelect = (item) => {
    const { pathname, query } = resolverLocation
    const value = item.get('value')

    // GA4 event
    setEventDefault(FILTER_APPLIED_GA4_EVENT.mergeDeep({
      ui_element: 'Seasons Filter',
      ui_location: uiLocation,
      filter_setting: `Season ${value}`,
    }))

    historyRedirect({
      history,
      url: pathname,
      query: _assign({}, query, { season: value }),
      auth,
      language: userLanguage,
      historyMethod: HISTORY_METHOD_REPLACE,
    })
    setSelectedSeason(value)
  }

  return (
    <div className="detail-series-seasons">
      {showAvailableEpisodes && (
        <div className="detail-series-seasons__available-episodes">
          <H5 className="detail-series-seasons__title">
            {staticText.get('available')}
          </H5>
          <DetailEpisodeList
            detailData={detailData || Map()}
            episodeData={availableEpisodeList}
            uiLocation={uiLocation}
          />
        </div>
      )}
      <div className="detail-series-seasons__all-episodes">
        {
          !loading && episodeTileData.size
            ? <H5 className="detail-series-seasons__title">
                {isGuideDetailPage ? staticText.get('allLessons') : staticText.get('allEpisodes')}
              </H5>
            : null
        }
        {seasonsOptions.size > 1 && !isGuideDetailPage && (
          <div className="detail-series-seasons__filter">
            <SelectField
              options={seasonsOptions}
              onClick={handleSeasonSelect}
              defaultValue={selectedSeason}
            />
          </div>
        )}
        {isGuideOrEvent
          && !hasLiveAccess
          && !(availableEpisodeList.size === episodeTileData.size) && (
          <DetailSeriesSeasonsJoinBanner {...props} />
        )}
        <DetailEpisodeList
          detailData={detailData || Map()}
          episodeData={episodeTileData}
          uiLocation={uiLocation}
        />
      </div>
    </div>
  )
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        auth,
        user,
        resolver,
      } = state
      const { detailData = Map() } = props
      const contentType = detailData.get('contentType')
      const contentSubtype = detailData.get('contentSubtype')
      const seriesContentSubtype = detailData.get('seriesContentSubtype')
      const displayType = detailData.get('displayType')
      const isEpisodeDetailPage = isEpisode({ contentType, data: detailData })
      const isEvent = isEpisodeDetailPage
        ? isSubtypeEvent({ contentSubtype: seriesContentSubtype })
        : isSubtypeEvent({ contentSubtype })
      const subscriptions = auth.get('subscriptions')
      const hasLiveAccess = hasLiveAccessEntitlement(subscriptions)

      return {
        auth,
        resolverLocation: resolver.get('location', Map()),
        staticText: state.staticText.getIn(['data', DETAIL_PAGE_STATIC_KEY, 'data']),
        isGuideDetailPage: isGuide({ contentSubtype, displayType }),
        isEpisodeDetailPage,
        userLanguage: getPrimary(user.getIn(['data', 'language'])),
        isEvent,
        hasLiveAccess,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(DetailSeriesSeasons)
